import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Save, Loader2, CheckCircle, RefreshCw, Eye, TrendingDown } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ScoreCard from '../components/ScoreCard';
import CriticalOverrideBanner from '../components/CriticalOverrideBanner';
import VelocityPlan from '../components/VelocityPlan';
import NarrativeReport from '../components/NarrativeReport';
import ProvenanceBadge from '../components/ProvenanceBadge';
import { buildVelocityPlan, buildNarrative, saveRun } from '../api';

const TABS = [
    { id: 'score',     label: 'Scores',        cta: 'the score breakdown' },
    { id: 'narrative', label: 'Narrative',     cta: 'the written analysis' },
    { id: 'velocity',  label: 'Velocity Plan', cta: 'the action steps' },
    { id: 'impacts',   label: 'Impacts',       cta: 'the variable impacts' },
];

export default function ResultStep({ property, inputs, uploadedDocuments = [], scoreResponse, isRestoredRun = false, onBack, onStartOver, onRunAgain }) {
    const [activeTab, setActiveTab] = useState('score');
    const [saved, setSaved] = useState(false);
    const qc = useQueryClient();

    // _restored values (from a saved-run reopen) bypass the API entirely.
    const restoredPlan = scoreResponse?._restored?.velocity_plan || null;
    const restoredNarrative = scoreResponse?._restored?.narrative || null;

    const { mutate: persist, isPending: saving } = useMutation({
        mutationFn: async () => {
            // Build the full run snapshot — property metadata + per-variable inputs
            // (profitability + affordability) + every document uploaded during the
            // run, so a reopened run can reconstruct the entire form state and
            // show what evidence was used.
            const runSnapshot = {
                property,
                profitability: inputs?.profitability || {},
                affordability: inputs?.affordability || {},
                uploaded_documents: uploadedDocuments,
            };
            const res = await saveRun({
                property_id: property.property_id,
                property_name: property.property_id,
                address: property.address,
                zip_code: property.zip_code,
                program_type: (property.programs || [])[0],
                inputs: runSnapshot,
                scores: { ...scoreResponse.scores, summary_card: scoreResponse.summary_card, impacts: scoreResponse.impacts, critical_override: scoreResponse.critical_override },
                provenance: Object.fromEntries(scoreResponse.impacts.map(i => [i.variable, i.provenance])),
                velocity_plan: plan || undefined,
                narrative: narrative || undefined,
            });
            if (!res.ok) {
                let detail = 'Save failed';
                try { detail = (await res.json()).error || detail; } catch (_) {}
                throw new Error(detail);
            }
            return res.json();
        },
        onSuccess: () => {
            setSaved(true);
            qc.invalidateQueries({ queryKey: ['profitabilityRuns'] });
        }
    });

    // Backend PropertyMeta requires units>=1 — saved runs from before we
    // started persisting the full property snapshot can be missing it. Fall
    // back to summary_card.units (always present from the original score) and
    // finally to 1 so neither the velocity-plan nor narrative call 422s.
    const propertyForApi = {
        ...property,
        units: property.units || scoreResponse?.summary_card?.units || 1,
    };

    // Velocity Plan + Narrative — both prefetch on mount as soon as we have a
    // score, since Pro narrative is genuinely slow (8–15s) and most users
    // open both tabs. Cached at Infinity so switching tabs doesn't re-fire.
    // Skipped entirely when reopening a saved run that has these stored.
    const planQuery = useQuery({
        queryKey: ['profitability-velocity-plan', property?.property_id, scoreResponse?.scores?.jpro, scoreResponse?.scores?.tier],
        queryFn: async () => {
            const res = await buildVelocityPlan({ score_response: scoreResponse, property: propertyForApi });
            if (!res.ok) throw new Error('Velocity plan generation failed');
            return await res.json();
        },
        enabled: !!scoreResponse && !restoredPlan,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });
    const plan = restoredPlan || planQuery.data || null;
    const planLoading = !restoredPlan && planQuery.isFetching;

    const narrativeQuery = useQuery({
        queryKey: ['profitability-narrative', property?.property_id, scoreResponse?.scores?.jpro, scoreResponse?.scores?.tier],
        queryFn: async () => {
            const res = await buildNarrative({ score_response: scoreResponse, property: propertyForApi });
            if (!res.ok) throw new Error('Narrative generation failed');
            return await res.json();
        },
        enabled: !!scoreResponse && !restoredNarrative,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });
    const narrative = restoredNarrative || narrativeQuery.data?.narrative || null;
    const enhancedRisks = narrativeQuery.data?.enhanced_risks || [];
    const narrativeLoading = !restoredNarrative && narrativeQuery.isFetching;

    return (
        <div>
            <CriticalOverrideBanner override={scoreResponse.critical_override} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="lg:col-span-1">
                    <ScoreCard
                        scores={scoreResponse.scores}
                        tier={scoreResponse.scores?.tier}
                        confidence={scoreResponse.scores?.confidence}
                        cappedByConfidenceGate={scoreResponse.scores?.capped_by_confidence_gate}
                        criticalOverride={scoreResponse.critical_override}
                        impacts={scoreResponse.impacts}
                    />
                </div>
                <div className="lg:col-span-2">
                    <div className="border-b border-slate-200 dark:border-slate-700 flex">
                        {TABS.map(t => {
                            const showSpinner =
                                (t.id === 'narrative' && narrativeLoading && activeTab !== 'narrative') ||
                                (t.id === 'velocity'  && planLoading      && activeTab !== 'velocity');
                            return (
                                <button key={t.id} onClick={() => setActiveTab(t.id)}
                                        className={`inline-flex items-center gap-1.5 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 rounded-t-md transition-colors
                                                    ${activeTab === t.id
                                                        ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                                                        : 'border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'}`}>
                                    {t.label}
                                    {showSpinner && <Loader2 size={12} className="animate-spin text-blue-500" />}
                                </button>
                            );
                        })}
                    </div>
                    <div className="pt-4">
                        {activeTab === 'score'     && <TopRisks scoreResponse={scoreResponse} />}
                        {activeTab === 'narrative' && <NarrativeReport narrative={narrative} enhancedRisks={enhancedRisks} loading={narrativeLoading} />}
                        {activeTab === 'velocity'  && <VelocityPlan plan={plan} loading={planLoading} />}
                        {activeTab === 'impacts'   && <ImpactsTable impacts={scoreResponse.impacts} />}
                    </div>
                    <NextTabHint activeTab={activeTab} onNext={setActiveTab} />
                </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700 flex-wrap gap-3">
                {isRestoredRun ? (
                    <div className="inline-flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <Eye size={14} /> Viewing a saved run — edits create a new revision
                    </div>
                ) : (
                    <button onClick={onBack} className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white">
                        <ArrowLeft size={16} /> Edit Inputs
                    </button>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                    <button onClick={onStartOver} className="text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white">
                        Start over
                    </button>
                    {isRestoredRun && onRunAgain && (
                        <button onClick={onRunAgain}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-blue-300 dark:border-blue-800 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950 text-sm font-medium transition-colors">
                            <RefreshCw size={14} /> Run again with this property
                        </button>
                    )}
                    <button onClick={() => persist()} disabled={saving || saved}
                            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-md disabled:opacity-50 shadow-sm transition-colors">
                        {saving ? <Loader2 className="animate-spin" size={16} /> : saved ? <CheckCircle size={16} /> : <Save size={16} />}
                        {saved ? 'Saved' : saving ? 'Saving…' : (isRestoredRun ? 'Save as New' : 'Save Run')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Contextual cue at the bottom of each tab nudging the user to the next one.
// On the last tab there's nowhere to go, so it renders nothing.
function NextTabHint({ activeTab, onNext }) {
    const idx = TABS.findIndex(t => t.id === activeTab);
    const next = TABS[idx + 1];
    if (!next) return null;
    return (
        <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex justify-end">
            <button onClick={() => onNext(next.id)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors">
                Click {next.label} for {next.cta}
                <ArrowRight size={14} />
            </button>
        </div>
    );
}

// Impact text color scales with how many points the risk costs. Color lives in
// the text only — no colored fills, borders, or backgrounds. Literal class
// strings so Tailwind's JIT emits them.
function riskImpactColor(impact) {
    const sev = Math.abs(Number(impact) || 0);
    if (sev >= 8) return 'text-red-600 dark:text-red-400';
    if (sev >= 4) return 'text-orange-600 dark:text-orange-400';
    return 'text-slate-600 dark:text-slate-300';
}

function TopRisks({ scoreResponse }) {
    const risks = (scoreResponse.summary_card?.top_risks || []);
    if (risks.length === 0) return <div className="text-sm text-slate-500">No significant risks identified.</div>;
    return (
        <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 dark:text-slate-400">Top Risks</div>
            {risks.map((r, i) => {
                const impactColor = riskImpactColor(r.impact);
                return (
                    <div key={i}
                         className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-3 transition-colors hover:border-slate-300 dark:hover:border-slate-600">
                        <span className="mt-0.5 w-4 flex-shrink-0 text-sm font-semibold tabular-nums text-slate-400 dark:text-slate-500">
                            {i + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className="font-semibold text-slate-800 dark:text-slate-100">{r.label}</div>
                            {r.rule_triggered && (
                                <div className="mt-0.5 text-xs leading-snug text-slate-500 dark:text-slate-400">{r.rule_triggered}</div>
                            )}
                        </div>
                        <span className={`inline-flex flex-shrink-0 items-center gap-1 text-sm font-semibold tabular-nums ${impactColor}`}>
                            <TrendingDown size={13} />
                            {r.impact} pts
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function ImpactsTable({ impacts }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                        <th className="py-2 pr-3">Variable</th>
                        <th className="py-2 pr-3">Rating</th>
                        <th className="py-2 pr-3">Provenance</th>
                        <th className="py-2 pr-3 text-right">Impact</th>
                    </tr>
                </thead>
                <tbody>
                    {impacts.map((i) => (
                        <tr key={i.variable} className="border-b border-slate-100 dark:border-slate-800">
                            <td className="py-2 pr-3">
                                <div className="text-slate-800 dark:text-slate-100">{i.label}</div>
                                {(i.driver_building || i.source) && (
                                    <div className="text-[11px] text-slate-500 dark:text-slate-400 tracking-tight">
                                        {i.driver_building && <>driver: <span className="font-medium text-slate-700 dark:text-slate-200">{i.driver_building}</span></>}
                                        {i.driver_building && i.source ? ' · ' : ''}
                                        {i.source && <>source: {i.source}</>}
                                    </div>
                                )}
                            </td>
                            <td className="py-2 pr-3 text-slate-600 dark:text-slate-300">{i.rating || '—'}</td>
                            <td className="py-2 pr-3"><ProvenanceBadge provenance={i.provenance} /></td>
                            <td className={`py-2 pr-3 text-right font-medium tabular-nums ${i.impact < 0 ? 'text-red-600 dark:text-red-400' : i.impact > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-500'}`}>
                                {i.impact > 0 ? '+' : ''}{i.impact}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
